<template>
  <div class="max-w-5xl mx-auto px-4 py-6">
    <!-- 标题 -->
    <div class="mb-5">
      <h1 class="text-2xl font-bold text-neutral-900 flex items-center">
        <span class="mr-2">🔬</span>小义AI科学分析台
      </h1>
      <p class="text-neutral-500 mt-2 text-sm leading-relaxed">
        功能：① 根据图片识别实验采集的数据；② 图显实验数据；③ 根据数据进行预测与分析。
      </p>
    </div>

    <!-- 上传区 -->
    <div class="bg-white border border-neutral-200 rounded-xl p-4 mb-4">
      <div class="flex flex-wrap items-center gap-3">
        <label class="inline-block cursor-pointer">
          <input
            type="file"
            accept="image/*"
            multiple
            class="hidden"
            :disabled="busy"
            @change="onPickImages"
          />
          <span
            class="inline-flex items-center px-4 py-2 bg-violet-600 text-white rounded-lg text-sm hover:bg-violet-700 transition"
          >
            <i class="fa fa-camera mr-1.5"></i>
            {{ busy ? "正在识别中…" : "上传实验图片（可多选）" }}
          </span>
        </label>
        <button
          v-if="images.length"
          class="ml-auto text-xs text-neutral-500 hover:text-red-500"
          @click="clearAll"
        >
          清空全部
        </button>
      </div>
    </div>

    <!-- 选中组：切换 + 曲线 + 数据表 -->
    <div v-if="images.length" class="bg-white border border-neutral-200 rounded-xl p-2.5 sm:p-4 mb-4">
      <div class="flex flex-wrap items-center gap-2 mb-2">
        <button
          v-for="(img, ii) in images"
          :key="'tab' + img.id"
          class="px-3 py-1.5 rounded-lg text-xs border transition"
          :class="
            selected === ii
              ? 'bg-neutral-900 text-white border-neutral-900'
              : 'bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-50'
          "
          @click="selectGroup(ii)"
        >
          {{ img.name || "第" + (ii + 1) + "组" }}
        </button>
        <div class="ml-auto flex items-center gap-2">
          <button
            v-if="selectedImage && selectedImage.conditions.length === groupNames.length"
            class="px-2.5 py-1.5 border border-neutral-200 rounded-lg text-xs text-neutral-600 hover:bg-neutral-50"
            title="若识别的行列方向反了，点这里互换"
            @click="transpose(selectedImage)"
          >
            ⇄ 转置
          </button>
        </div>
      </div>

      <template v-if="selectedImage">
        <div v-if="selectedImage.ocr.running" class="text-xs text-violet-600 mb-2">
          <i class="fa fa-spinner fa-spin mr-1"></i>{{ selectedImage.ocr.msg }}
        </div>
        <div
          v-else-if="selectedImage.ocr.msg"
          class="text-xs mb-2"
          :class="selectedImage.ocr.error ? 'text-red-500' : 'text-emerald-600'"
        >
          <i
            :class="selectedImage.ocr.error ? 'fa fa-exclamation-circle' : 'fa fa-check-circle'"
            class="mr-1"
          ></i>{{ selectedImage.ocr.msg }}
        </div>

        <!-- 该组曲线 -->
        <MultiLineChart
          :series="seriesOf(selectedImage)"
          :x-labels="condNames"
          x-title="实验条件"
          y-title="温度 (℃)"
        />

        <!-- 该组数据表：行＝实验条件，列＝实验组 -->
        <div class="mt-2 flex items-center gap-1.5">
          <input
            v-model="selectedImage.name"
            class="min-w-0 flex-1 px-1 py-0.5 text-xs font-bold text-primary border-b border-transparent hover:border-neutral-300 focus:border-primary focus:outline-none"
          />
          <span class="shrink-0 text-[10px] text-neutral-400 hidden sm:inline">
            行＝实验条件，列＝实验组
          </span>
          <button
            class="shrink-0 text-neutral-400 hover:text-red-500 text-xs"
            title="删除这一组"
            @click="removeImage(selected)"
          >
            <i class="fa fa-trash-o"></i>
          </button>
        </div>

        <table class="mt-1 w-full table-fixed text-[11px] leading-tight border-collapse">
          <thead>
            <tr class="text-neutral-500">
              <th
                class="w-[36%] px-1 py-1 text-left font-normal border-b border-neutral-200 align-bottom"
              >
                <span class="inline-flex items-center gap-1">
                  实验条件
                  <button
                    class="w-4 h-4 leading-none rounded border border-neutral-200 text-[10px] text-neutral-500 hover:border-primary hover:text-primary"
                    title="添加一行（实验条件）"
                    @click="addCondition"
                  >
                    +
                  </button>
                </span>
              </th>
              <th
                v-for="(g, gi) in groupNames"
                :key="'hg' + gi"
                class="px-1 py-1 text-left font-normal border-b border-neutral-200 align-bottom"
              >
                <span class="inline-flex items-center gap-0.5 w-full min-w-0">
                  <span
                    class="shrink-0 w-1.5 h-1.5 rounded-full"
                    :style="{ background: condColors[gi % condColors.length] }"
                  ></span>
                  <input
                    v-model="groupNames[gi]"
                    class="min-w-0 w-full px-0.5 py-0.5 text-[11px] border-b border-transparent hover:border-neutral-300 focus:border-primary focus:outline-none"
                  />
                  <button
                    v-if="groupNames.length > 1"
                    class="shrink-0 text-neutral-300 hover:text-red-500 text-[10px]"
                    title="删除这一列（实验组）"
                    @click="removeGroup(gi)"
                  >
                    <i class="fa fa-times"></i>
                  </button>
                </span>
              </th>
              <th class="w-6 px-0.5 py-1 border-b border-neutral-200 align-bottom">
                <button
                  class="w-4 h-4 leading-none rounded border border-neutral-200 text-[10px] text-neutral-500 hover:border-primary hover:text-primary"
                  title="添加一列（实验组）"
                  @click="addGroup"
                >
                  +
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(c, t) in condNames"
              :key="'c' + t"
              class="hover:bg-neutral-50"
            >
              <td class="px-1 py-0.5 border-b border-neutral-100">
                <span class="inline-flex items-center gap-0.5 w-full min-w-0">
                  <input
                    v-model="condNames[t]"
                    class="min-w-0 w-full px-1 py-0.5 text-[11px] border border-neutral-200 rounded text-neutral-700 focus:border-primary focus:outline-none"
                  />
                  <button
                    v-if="condNames.length > 1"
                    class="shrink-0 text-neutral-300 hover:text-red-500 text-[10px]"
                    title="删除这一行（实验条件）"
                    @click="removeCondition(t)"
                  >
                    <i class="fa fa-times"></i>
                  </button>
                </span>
              </td>
              <td
                v-for="(g, gi) in groupNames"
                :key="'v' + t + '-' + gi"
                class="px-0.5 py-0.5 border-b border-neutral-100"
              >
                <input
                  :value="cellValue(selectedImage, t, gi)"
                  type="number"
                  step="0.1"
                  inputmode="decimal"
                  class="w-full min-w-0 px-0.5 py-0.5 text-[11px] text-center border border-neutral-200 rounded focus:border-primary focus:outline-none"
                  @change="setCell(selectedImage, t, gi, $event.target.value)"
                />
              </td>
              <td class="px-0.5 py-0.5 border-b border-neutral-100"></td>
            </tr>

            <!-- 温差行：每列（实验组）最后一个数据 − 第一个数据 -->
            <tr class="bg-amber-50/50">
              <td
                class="px-1 py-1 border-b border-neutral-100 whitespace-nowrap text-amber-700 font-medium"
                title="每列（实验组）最后一个数据 − 第一个数据（即第三个数据 − 第一个数据）"
              >
                温差
              </td>
              <td
                v-for="(d, gi) in groupDiff(selectedImage).perGroup"
                :key="'d' + gi"
                class="px-0.5 py-1 border-b border-neutral-100 text-center text-amber-700 font-semibold truncate"
              >
                {{ formatNum(d.value) }}
              </td>
              <td class="px-0.5 py-1 border-b border-neutral-100"></td>
            </tr>
          </tbody>
        </table>

        <details v-if="selectedImage.ocr.text" class="mt-2 text-xs">
          <summary class="text-neutral-400 cursor-pointer hover:text-neutral-600">查看识别原文</summary>
          <pre
            class="mt-1 text-[10px] bg-neutral-50 p-2 rounded max-h-32 overflow-auto whitespace-pre-wrap font-mono"
            >{{ selectedImage.ocr.text }}</pre
          >
        </details>
      </template>
    </div>

    <!-- 各组数据与温差汇总表 -->
    <div v-if="images.length" class="bg-white border border-neutral-200 rounded-xl p-2.5 sm:p-4 mb-4">
      <div class="flex items-center gap-2 mb-1.5">
        <span
          class="w-6 h-6 rounded-full bg-sky-600 text-white text-xs flex items-center justify-center font-bold"
        >
          <i class="fa fa-table"></i>
        </span>
        <h2 class="text-sm font-semibold text-neutral-900">数据与温差汇总</h2>
        <span class="text-[11px] text-neutral-400">
          {{ images.length }} 组 · 行＝实验条件
        </span>
      </div>

      <table class="w-full table-fixed text-[10px] sm:text-xs border-collapse">
        <thead>
          <tr class="text-neutral-500">
            <th class="w-[20%] px-1 py-1 text-left font-normal border-b border-neutral-200">组别</th>
            <th class="w-[24%] px-1 py-1 text-left font-normal border-b border-neutral-200">
              实验条件
            </th>
            <th
              v-for="(g, gi) in groupNames"
              :key="'sg' + gi"
              class="px-1 py-1 text-left font-normal border-b border-neutral-200"
            >
              <span class="inline-flex items-center min-w-0">
                <span
                  class="shrink-0 inline-block w-1.5 h-1.5 rounded-full mr-1"
                  :style="{ background: condColors[gi % condColors.length] }"
                ></span>
                <span class="truncate">{{ g }}</span>
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(img, ii) in images" :key="'sgrp' + img.id">
            <tr
              v-for="(c, t) in condNames"
              :key="'sr' + img.id + '-' + t"
              class="hover:bg-neutral-50"
            >
              <td
                v-if="t === 0"
                :rowspan="condNames.length + 1"
                class="px-1 py-1 border-b border-neutral-100 align-middle"
              >
                <span class="block truncate font-medium text-neutral-700">
                  {{ img.name || "第" + (ii + 1) + "组" }}
                </span>
                <span v-if="!dataCount(img)" class="block text-[10px] text-red-400 truncate">
                  未识别到数据
                </span>
              </td>
              <td class="px-1 py-1 border-b border-neutral-100 truncate text-neutral-600">
                {{ c }}
              </td>
              <td
                v-for="(g, gi) in groupNames"
                :key="'sv' + img.id + '-' + t + '-' + gi"
                class="px-1 py-1 border-b border-neutral-100 text-center text-neutral-700"
              >
                {{ formatNum(cellValue(img, t, gi)) }}
              </td>
            </tr>
            <tr class="bg-amber-50/50">
              <td class="px-1 py-1 border-b border-neutral-100 text-amber-700 font-medium">温差</td>
              <td
                v-for="(d, gi) in groupDiff(img).perGroup"
                :key="'sd' + img.id + '-' + gi"
                class="px-1 py-1 border-b border-neutral-100 text-center text-amber-700 font-semibold truncate"
              >
                {{ formatNum(d.value) }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- 汇总图 -->
    <div v-if="validImages.length" class="bg-white border border-violet-200 rounded-xl p-4 mb-4">
      <div class="flex items-center gap-2 mb-1">
        <span class="w-6 h-6 rounded-full bg-violet-600 text-white text-xs flex items-center justify-center font-bold">
          <i class="fa fa-flag-checkered"></i>
        </span>
        <h2 class="text-sm font-semibold text-neutral-900">汇总图 · 各组平均降温对比</h2>
        <span class="text-xs text-neutral-400">{{ validImages.length }} 条曲线</span>
      </div>

      <div class="mt-1">
        <table class="w-full table-fixed text-[10px] sm:text-xs border-collapse">
          <thead>
            <tr class="text-neutral-500">
              <th class="px-1 py-1 sm:px-2 sm:py-1.5 text-left font-normal border-b border-neutral-200">
                实验组
              </th>
              <th class="px-1 py-1 sm:px-2 sm:py-1.5 text-left font-normal border-b border-neutral-200">
                平均降温值
              </th>
              <th class="px-1 py-1 sm:px-2 sm:py-1.5 text-left font-normal border-b border-neutral-200">
                预测下一个点
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(p, i) in groupStats" :key="'pd' + i" class="hover:bg-neutral-50">
              <td class="px-1 py-1 sm:px-2 sm:py-1.5">
                <span class="inline-flex items-center min-w-0">
                  <span
                    class="inline-block w-1.5 h-1.5 shrink-0 rounded-full mr-1"
                    :style="{ background: p.color }"
                  ></span>
                  <span class="truncate">{{ p.name }}</span>
                </span>
              </td>
              <td class="px-1 py-1 sm:px-2 sm:py-1.5 text-neutral-700 whitespace-nowrap">
                {{ formatNum(p.last) }}
              </td>
              <td class="px-1 py-1 sm:px-2 sm:py-1.5 font-semibold text-violet-600 whitespace-nowrap">
                {{ formatNum(p.next) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mt-3">
        <h3 class="text-xs font-semibold text-neutral-600 mb-1">各学生组平均降温曲线</h3>
        <MultiLineChart
          :series="summarySeries"
          :x-labels="summaryXLabels"
          x-title="实验条件"
          y-title="平均降温 (℃)"
        />
      </div>

      <!-- 各组平均降温柱状图 -->
      <div v-if="dropItems.length" class="mt-3">
        <div class="flex items-center gap-2 mb-1">
          <h3 class="text-xs font-semibold text-violet-700">各实验组平均降温对比</h3>
          <span class="text-[11px] text-neutral-400">每组为三组学生数据的平均</span>
        </div>
        <BarChart :items="dropItems" y-title="平均降温 (℃)" />
      </div>

      <!-- 预测原因 -->
      <div class="mt-3 bg-violet-50 rounded-lg p-3">
        <div class="flex items-center gap-2 mb-1.5">
          <span class="text-xs font-medium text-violet-700">为什么这样预测</span>
          <button
            class="text-xs text-violet-600 hover:underline"
            :disabled="explaining"
            @click="explainPrediction"
          >
            {{ explaining ? "AI 正在分析…" : "让 AI 深入解释" }}
          </button>
        </div>
        <p class="text-xs text-neutral-700 leading-relaxed whitespace-pre-wrap">
          {{ explanation || localExplanation }}
        </p>
      </div>
    </div>

    <!-- 探究对话 -->
    <div v-if="validImages.length" class="bg-white border border-neutral-200 rounded-xl p-4">
      <h2 class="text-sm font-semibold text-neutral-900 mb-2 flex items-center">
        <i class="fa fa-comments text-primary mr-1.5"></i>探究互动对话
      </h2>

      <div class="space-y-2 max-h-80 overflow-y-auto mb-3">
        <div
          v-for="(m, i) in messages"
          :key="'m' + i"
          class="flex"
          :class="m.role === 'user' ? 'justify-end' : 'justify-start'"
        >
          <div
            class="max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed whitespace-pre-wrap"
            :class="m.role === 'user' ? 'bg-primary text-white' : 'bg-neutral-100 text-neutral-700'"
          >
            {{ m.text }}
          </div>
        </div>
        <div v-if="sending" class="text-xs text-neutral-400">
          <i class="fa fa-spinner fa-spin mr-1"></i>AI 正在思考…
        </div>
      </div>

      <div class="flex gap-2">
        <input
          v-model="question"
          class="flex-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:border-primary focus:outline-none"
          placeholder="输入你的问题，例如：为什么第二组的温度降得最快？"
          :disabled="sending"
          @keyup.enter="sendQuestion"
        />
        <button
          class="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50"
          :disabled="sending || !question.trim()"
          @click="sendQuestion"
        >
          发送
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import BarChart from "../components/BarChart.vue";
import MultiLineChart from "../components/MultiLineChart.vue";

const COND_COLORS = ["#0ea5e9", "#f59e0b", "#10b981"];
const SUMMARY_COLORS = [
  "#0ea5e9",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#8b5cf6",
  "#14b8a6",
  "#f97316",
  "#6366f1",
];

let seed = 0;

export default {
  name: "DataAnalysisPage",
  components: { BarChart, MultiLineChart },
  data() {
    return {
      images: [],
      condNames: ["初温", "30s水温", "1min水温"],
      groupNames: ["实验组A", "实验组B", "对照组"],
      condColors: COND_COLORS,
      selected: 0,
      busy: false,
      explanation: "",
      explaining: false,
      messages: [],
      question: "",
      sending: false,
    };
  },
  computed: {
    validImages() {
      return this.images.filter((img) => this.dataCount(img) > 0);
    },
    selectedImage() {
      return this.images[this.selected] || this.images[0] || null;
    },
    /** 柱状图数据：各实验组的平均降温值（三组学生数据平均） */
    dropItems() {
      return this.groupStats
        .filter((p) => p.last !== null && isFinite(p.last))
        .map((p) => ({ name: p.name, value: p.last, color: p.color }));
    },
    summaryXLabels() {
      const n = this.maxConditionCount;
      const labels = this.condNames.slice(0, Math.max(n, 3));
      labels.push("预测");
      return labels;
    },
    maxConditionCount() {
      return this.images.reduce((m, img) => Math.max(m, (img.conditions || []).length), 0);
    },
    summarySeries() {
      return this.images.map((img, i) => {
        const pts = this.avgPoints(img);
        const pd = this.regress(pts);
        const color = SUMMARY_COLORS[i % SUMMARY_COLORS.length];
        return {
          name: img.name || `第${i + 1}组`,
          color,
          points: pts,
          dashed: true,
          width: 2.4,
          predict: pd.next ? { x: pd.next.x, y: pd.next.y } : null,
          showValue: pts.length <= 6,
        };
      });
    },
    /**
     * 各实验组（处理）统计：同一实验组跨所有学生组取平均。
     * 降温 = 初温 − 当前温度；平均降温值取最后一个已测条件，
     * 预测下一个点用该实验组的平均降温序列做线性外推。
     */
    groupStats() {
      return this.groupNames.map((gname, gi) => {
        const buckets = new Map();
        this.validImages.forEach((img) => {
          const conds = (img.conditions || []).map((arr) => arr || []);
          const base = this.numOrNull((conds[0] || [])[gi]);
          if (base === null) return;
          conds.forEach((arr, t) => {
            const v = this.numOrNull(arr[gi]);
            if (v === null) return;
            if (!buckets.has(t)) buckets.set(t, []);
            buckets.get(t).push(base - v);
          });
        });
        const pts = [...buckets.entries()]
          .sort((a, b) => a[0] - b[0])
          .map(([x, ys]) => ({
            x,
            y: Math.round((ys.reduce((s, v) => s + v, 0) / ys.length) * 100) / 100,
          }));
        const pd = this.regress(pts);
        return {
          name: gname || `实验组${gi + 1}`,
          color: this.condColors[gi % this.condColors.length],
          last: pts.length ? pts[pts.length - 1].y : null,
          next: pd.next ? pd.next.y : null,
          slope: pd.slope,
          r2: pd.r2,
        };
      });
    },
    localExplanation() {
      const list = this.groupStats.filter((p) => p.last !== null && p.next !== null);
      if (!list.length) return "";
      const lines = list.map((p) => {
        const delta = p.next - p.last;
        const dir = delta > 0.05 ? "上升" : delta < -0.05 ? "下降" : "基本持平";
        return `${p.name}：平均降温 ${this.formatNum(p.last)}℃，线性趋势${this.trendText(
          p.slope
        )}，预测下一个点为 ${this.formatNum(p.next)}℃（${dir} ${this.formatNum(
          Math.abs(delta)
        )}℃，R²=${p.r2.toFixed(3)}）`;
      });
      return (
        lines.join("\n") +
        "\n\n依据：先把每个学生组的数据换算成相对初温的降温量，再按实验组求所有学生组的平均，得到该实验组的平均降温序列；" +
        "用最小二乘法对这条序列做线性拟合，取斜率和截距外推到下一个实验条件。" +
        "R² 越接近 1 说明降温越接近线性变化、预测越可靠；R² 偏低说明降温不规律（如接近环境温度趋于平缓），此时预测仅供趋势参考。"
      );
    },
    dataContext() {
      if (!this.validImages.length) return "";
      return this.validImages
        .map((img, i) => {
          const rows = (img.conditions || [])
            .map((arr, t) => {
              const vals = (arr || [])
                .filter((v) => v !== null && v !== "" && isFinite(Number(v)))
                .map((v) => this.formatNum(v));
              return vals.length
                ? `${this.condNames[t] || "实验条件" + (t + 1)}（${this.groupNames.join("、")}）: ${vals.join(", ")}`
                : "";
            })
            .filter(Boolean);
          const avg = this.avgPoints(img)
            .map((p) => this.formatNum(p.y))
            .join(", ");
          const pd = this.regress(this.avgPoints(img));
          return `【${img.name || "第" + (i + 1) + "组"}】${rows.join("；")}；平均降温序列: ${avg}；预测下一点: ${
            pd.next ? this.formatNum(pd.next.y) + "℃" : "无法预测"
          }`;
        })
        .join("\n");
    },
  },
  methods: {
    /* ---------- 数据工具 ---------- */
    ensureCondNames(n) {
      while (this.condNames.length < n) {
        this.condNames.push("实验条件" + (this.condNames.length + 1));
      }
    },
    selectGroup(ii) {
      this.selected = ii;
    },
    dataCount(img) {
      return (img.conditions || []).reduce(
        (s, arr) =>
          s + (arr || []).filter((v) => v !== null && v !== "" && isFinite(Number(v))).length,
        0
      );
    },
    cellValue(img, t, gi) {
      const arr = img.conditions[t] || [];
      const v = arr[gi];
      return v === null || v === undefined || v === "" ? "" : v;
    },
    /** 转数值，空值/非数字返回 null */
    numOrNull(v) {
      if (v === null || v === undefined || v === "") return null;
      const n = Number(v);
      return isFinite(n) ? n : null;
    },
    setCell(img, t, gi, raw) {
      while (img.conditions.length <= t) {
        img.conditions.push(new Array(this.groupNames.length).fill(null));
      }
      const arr = img.conditions[t];
      while (arr.length < this.groupNames.length) arr.push(null);
      const n = raw === "" ? null : Number(raw);
      this.$set(arr, gi, isNaN(n) ? null : n);
      this.explanation = "";
    },
    /** 一行空白数据（每个实验组一个格子） */
    blankRow() {
      return new Array(this.groupNames.length).fill(null);
    },
    /** 添加一行（实验条件） */
    addCondition() {
      this.condNames.push("实验条件" + (this.condNames.length + 1));
      this.images.forEach((img) => {
        while (img.conditions.length < this.condNames.length - 1) {
          img.conditions.push(this.blankRow());
        }
        img.conditions.push(this.blankRow());
      });
      this.explanation = "";
    },
    /** 删除一行（实验条件） */
    removeCondition(t) {
      if (this.condNames.length <= 1) return;
      this.condNames.splice(t, 1);
      this.images.forEach((img) => {
        if (img.conditions.length > t) img.conditions.splice(t, 1);
      });
      this.explanation = "";
    },
    /** 添加一列（实验组） */
    addGroup() {
      const used = this.groupNames.length;
      this.groupNames.push("实验组" + String.fromCharCode(65 + used));
      this.images.forEach((img) => {
        while (img.conditions.length < this.condNames.length) {
          img.conditions.push(new Array(used).fill(null));
        }
        img.conditions.forEach((arr) => {
          while (arr.length < used) arr.push(null);
          arr.push(null);
        });
      });
      this.explanation = "";
    },
    /** 删除一列（实验组） */
    removeGroup(gi) {
      if (this.groupNames.length <= 1) return;
      this.groupNames.splice(gi, 1);
      this.images.forEach((img) => {
        (img.conditions || []).forEach((arr) => {
          if (arr.length > gi) arr.splice(gi, 1);
        });
      });
      this.explanation = "";
    },
    /** 行列互换：模型把表格读反时（行=处理、列=时间点）用它一键纠正 */
    transpose(img) {
      const n = img.conditions.length;
      const m = this.groupNames.length;
      if (n !== m) {
        return;
      }
      const next = [];
      for (let i = 0; i < m; i++) {
        const row = [];
        for (let j = 0; j < n; j++) {
          const v = (img.conditions[j] || [])[i];
          row.push(v === undefined ? null : v);
        }
        next.push(row);
      }
      img.conditions = next;
      this.explanation = "";
    },
    removeImage(ii) {
      this.images.splice(ii, 1);
      if (this.selected >= this.images.length) {
        this.selected = Math.max(0, this.images.length - 1);
      }
      this.explanation = "";
    },
    clearAll() {
      this.images = [];
      this.selected = 0;
      this.messages = [];
      this.explanation = "";
    },
    /**
     * 平均值 = 同一组、同一实验条件下，三个处理「相对初温的降温量」的平均
     * （不是把温度直接相加求平均）
     */
    condDropAvg(img, t) {
      const init = img.conditions[0] || [];
      const cur = img.conditions[t] || [];
      const drops = [];
      for (let g = 0; g < this.groupNames.length; g++) {
        const a = init[g];
        const b = cur[g];
        const aOk = a !== null && a !== "" && isFinite(Number(a));
        const bOk = b !== null && b !== "" && isFinite(Number(b));
        if (aOk && bOk) drops.push(Number(a) - Number(b));
      }
      if (!drops.length) return null;
      return drops.reduce((x, y) => x + y, 0) / drops.length;
    },
    /**
     * 温差：每列（每个处理）最后一个数据 − 第一个数据（通常是 1min水温 − 初温），
     * 再对同一组内的各处理取平均
     */
    groupDiff(img) {
      const conds = (img && img.conditions) || [];
      // 取「已填数值」的首尾两行，避免增删行后误用空行
      const filled = conds
        .map((arr) => arr || [])
        .filter((arr) =>
          arr.some((v) => v !== null && v !== undefined && v !== "" && isFinite(Number(v)))
        );
      const first = filled.length ? filled[0] : [];
      const last = filled.length > 1 ? filled[filled.length - 1] : [];
      const perGroup = [];
      for (let g = 0; g < this.groupNames.length; g++) {
        const a = first[g];
        const b = last[g];
        const aOk = a !== null && a !== undefined && a !== "" && isFinite(Number(a));
        const bOk = b !== null && b !== undefined && b !== "" && isFinite(Number(b));
        perGroup.push({
          label: (this.groupNames[g] || "").replace(/^实验组/, ""),
          value: aOk && bOk ? Math.round((Number(b) - Number(a)) * 100) / 100 : null,
        });
      }
      const valid = perGroup.filter((d) => d.value !== null);
      const avg = valid.length
        ? Math.round((valid.reduce((s, d) => s + d.value, 0) / valid.length) * 100) / 100
        : null;
      return { perGroup, avg };
    },
    /** 单张图的曲线：每个处理（实验组A/B/对照组）沿时间连成一条线 */
    seriesOf(img) {
      const conds = img.conditions || [];
      return this.groupNames.map((gname, gi) => ({
        name: gname,
        color: this.condColors[gi % this.condColors.length],
        points: conds
          .map((arr, t) => ({ x: t, y: (arr || [])[gi] }))
          .filter((p) => p.y !== null && p.y !== "" && isFinite(Number(p.y)))
          .map((p) => ({ x: p.x, y: Number(p.y) })),
        showValue: false,
      }));
    },
    /** 平均值序列（每个实验条件下三个处理的平均降温量） */
    avgPoints(img) {
      const out = [];
      (img.conditions || []).forEach((arr, t) => {
        const v = this.condDropAvg(img, t);
        if (v !== null && isFinite(v)) out.push({ x: t, y: Math.round(v * 100) / 100 });
      });
      return out;
    },
    regress(points) {
      const n = points.length;
      if (n < 2) {
        return { slope: 0, r2: 0, next: null };
      }
      const xs = points.map((p) => p.x);
      const ys = points.map((p) => p.y);
      const mx = xs.reduce((a, b) => a + b, 0) / n;
      const my = ys.reduce((a, b) => a + b, 0) / n;
      let sxy = 0;
      let sxx = 0;
      for (let i = 0; i < n; i++) {
        sxy += (xs[i] - mx) * (ys[i] - my);
        sxx += (xs[i] - mx) ** 2;
      }
      const slope = sxx ? sxy / sxx : 0;
      const intercept = my - slope * mx;
      let ssTot = 0;
      let ssRes = 0;
      for (let i = 0; i < n; i++) {
        const pred = slope * xs[i] + intercept;
        ssTot += (ys[i] - my) ** 2;
        ssRes += (ys[i] - pred) ** 2;
      }
      const r2 = ssTot ? Math.max(0, 1 - ssRes / ssTot) : 0;
      const nextX = Math.max(...xs) + 1;
      return {
        slope,
        r2,
        next: { x: nextX, y: Math.round((slope * nextX + intercept) * 100) / 100 },
      };
    },
    trendText(slope) {
      if (slope > 0.05) return "上升";
      if (slope < -0.05) return "下降";
      return "基本持平";
    },
    trendClass(slope) {
      if (slope > 0.05) return "text-red-500";
      if (slope < -0.05) return "text-sky-600";
      return "text-neutral-500";
    },
    formatNum(v) {
      const n = Number(v);
      if (v === null || v === "" || isNaN(n)) return "-";
      return String(Math.round(n * 100) / 100);
    },

    /* ---------- 图片识别 ---------- */
    async onPickImages(e) {
      const files = Array.from(e.target.files || []);
      e.target.value = "";
      if (!files.length) return;
      this.busy = true;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const img = {
          id: ++seed,
          name: `第${this.images.length + 1}组`,
          conditions: this.condNames.map(() => this.blankRow()),
          ocr: { running: true, msg: "正在读取图片", text: "", error: false },
        };
        this.images.push(img);
        this.selected = this.images.length - 1;
        await this.recognizeInto(img, file);
      }
      this.busy = false;
      this.explanation = "";
    },
    async recognizeInto(img, file) {
      const st = img.ocr;
      st.running = true;
      st.error = false;
      st.text = "";
      try {
        const base64 = await this.toCompressedBase64(file);
        st.msg = "AI 正在识别图片中的数据…";
        const dataStr = await this.$aesEncrypt({
          image: base64,
          hint: `图中是学生实验记录表格：最上是 ${
            this.groupNames.length
          } 列（从左到右依次为 ${this.groupNames.join(
            "、"
          )}，表格上可能写作组1/组2/组3），左侧是初温/30s水温/1min水温等实验条件，中间是数值。请按行输出：每行（实验条件）包含这 ${
            this.groupNames.length
          } 列共 ${this.groupNames.length} 个数值，按从左到右顺序。`,
        });
        const res = await this.$axios({
          url: "analysis/ocr",
          method: "POST",
          data: { data: dataStr },
        });
        const groups = (res && res.data && Array.isArray(res.data.groups) && res.data.groups) || [];
        const rows = (res && res.data && res.data.rows) || [];
        st.text = (res && res.data && res.data.text) || "";

        const groupsValues = groups
          .map((g) => (g || []).map((r) => Number(r.y)).filter((v) => isFinite(v)))
          .filter((a) => a.length);

        if (groupsValues.length) {
          img.conditions = groupsValues.map((a) => a.slice(0, this.groupNames.length));
        } else if (rows.length) {
          const vals = rows.map((r) => Number(r.y)).filter((v) => isFinite(v));
          img.conditions = [vals.slice(0, this.groupNames.length)];
        }

        if (!img.conditions.length || !this.dataCount(img)) {
          st.error = true;
          st.msg = "没识别出数据，可点开「识别原文」看看模型返回了什么，或手动填写";
          return;
        }
        this.ensureCondNames(img.conditions.length);
        const total = this.dataCount(img);
        st.msg = `识别到 ${img.conditions.length} 个实验条件共 ${total} 个数值，请核对表格`;
      } catch (err) {
        st.error = true;
        st.msg =
          "识别失败：" +
          (err && err.msg ? err.msg : err && err.message ? err.message : "请稍后重试");
      } finally {
        st.running = false;
      }
    },
    toCompressedBase64(file, maxSide = 1600) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          try {
            const scale = Math.min(1, maxSide / Math.max(img.width || 1, img.height || 1));
            const c = document.createElement("canvas");
            c.width = Math.round(img.width * scale);
            c.height = Math.round(img.height * scale);
            const ctx = c.getContext("2d");
            ctx.drawImage(img, 0, 0, c.width, c.height);
            const dataUrl = c.toDataURL("image/jpeg", 0.85);
            URL.revokeObjectURL(img.src);
            resolve(dataUrl.split(",")[1] || dataUrl);
          } catch (e) {
            reject(new Error("图片处理失败"));
          }
        };
        img.onerror = () => reject(new Error("图片读取失败"));
        img.src = URL.createObjectURL(file);
      });
    },

    /* ---------- AI 解释与对话 ---------- */
    async explainPrediction() {
      if (this.explaining) return;
      this.explaining = true;
      try {
        const dataStr = await this.$aesEncrypt({
          question:
            "请解释：为什么各组的平均降温会呈现这样的变化趋势？预测的下一个实验条件是否合理？降温背后的物理原因是什么？",
          context: this.dataContext,
        });
        const res = await this.$axios({
          url: "analysis/chat",
          method: "POST",
          data: { data: dataStr },
        });
        this.explanation = (res && res.data && res.data.answer) || "";
      } catch (err) {
        this.explanation =
          "AI 解释失败：" +
          (err && err.msg ? err.msg : err && err.message ? err.message : "请稍后重试") +
          "\n（上面的线性拟合结论仍然有效）";
      } finally {
        this.explaining = false;
      }
    },
    async sendQuestion() {
      const q = this.question.trim();
      if (!q || this.sending) return;
      this.messages.push({ role: "user", text: q });
      this.question = "";
      this.sending = true;
      try {
        const dataStr = await this.$aesEncrypt({ question: q, context: this.dataContext });
        const res = await this.$axios({
          url: "analysis/chat",
          method: "POST",
          data: { data: dataStr },
        });
        this.messages.push({
          role: "assistant",
          text: (res && res.data && res.data.answer) || "（没有返回内容）",
        });
      } catch (err) {
        this.messages.push({
          role: "assistant",
          text:
            "回答失败：" +
            (err && err.msg ? err.msg : err && err.message ? err.message : "请稍后重试"),
        });
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>
